import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { DateTime } from "luxon";

import { useScreenWatcher } from "@common/screenWatcher";
import { HOURS_PER_DAY, Views } from "../api/views";
import { useScrollPos } from "../scrollPosContext";
import { Props } from "./types";
import { getDaysTexts, isRestDay } from "./utils";
import { Months } from "./months";
import { Years } from "./years";
import { DailyMonths } from "./dailyMonths";
import styles from "./styles.scss";

/**
 * Pairs with the Gantt-side change (optimizedGantt/index.tsx, horizontal
 * "graph" ScrollBar): quantize the store index and drop the rAF —
 *
 *   const CHUNK = 12; // columns per store step; must stay < OVERSCAN below
 *   scroll={(scrollPos) => {
 *     ...imperative pane syncing unchanged...
 *     scrollPosStore.set(
 *       Math.floor(scrollPos / views.cell.width / CHUNK) * CHUNK,
 *     );
 *   }}
 *
 * With CHUNK=12 / OVERSCAN=24 the rendered window always extends at least
 * 12 columns past both viewport edges, so a store update is never urgent
 * and intermediate scroll frames trigger no React work at all.
 */
const OVERSCAN = 24;

type DateCell = {
  dt: DateTime;
  key: string; // "2026-07-13" — stable key, computed once
  dayLabel: string; // zero-padded day-of-month, e.g. "07"
  dayIndex: number; // 0=Mon .. 6=Sun
  isRest: boolean;
  isMonthLastDay: boolean;
  isYearLastDay: boolean;
};

/**
 * All per-date facts, computed ONCE per range instead of per cell per render.
 *
 * INVARIANT: `dates` is a consecutive daily sequence (getDates produces one
 * entry per calendar day). That lets us derive month/year boundaries by
 * comparing each date to its NEIGHBOR — zero Luxon arithmetic, not even at
 * build time. If getDates ever gains gaps, fall back to
 * `dt.plus({ days: 1 }).month !== dt.month`.
 */
function buildDateCells(dates: DateTime[]): DateCell[] {
  return dates.map((dt, i) => {
    const next: DateTime | undefined = dates[i + 1];
    const dayIndex = dt.weekday - 1;
    const day = dt.day;
    return {
      dt,
      key: dt.toISODate() ?? `i${i}`, // toISODate is much cheaper than toFormat
      dayLabel: day < 10 ? `0${day}` : String(day),
      dayIndex,
      isRest: isRestDay(dayIndex),
      isMonthLastDay: next ? next.month !== dt.month : true,
      isYearLastDay: next ? next.year !== dt.year : true,
    };
  });
}

const headerMap = {
  [Views.DAILY]: DailyMonths,
  [Views.WEEKLY]: Months,
  [Views.MONTHLY]: Months,
  [Views.YEARLY]: Years,
};

// Hour labels for the daily view, precomputed once like shortDays.
const HOURS = Array.from({ length: HOURS_PER_DAY }, (_, hour) =>
  hour < 10 ? `0${hour}` : String(hour),
);

// Zooming out shrinks the day cell; below these per-hour widths the
// subdivision stops reading as columns — drop the labels first, then the
// gridlines, leaving plain day columns.
const MIN_HOUR_LABEL_WIDTH = 15;
const MIN_HOUR_LINE_WIDTH = 5;

type CellProps = {
  c: DateCell;
  name: string; // localized 3-letter day name
  isNow: boolean;
  width: number;
  columnHeight: number;
  cellHeight: number;
  currentView: Views;
};

/**
 * One ruler column. React.memo works here because `c` objects come from the
 * memoized `cells` array, so their identities are stable across renders:
 * when the window shifts, only the columns entering/leaving the window
 * mount/unmount — every column that stays put bails out.
 */
const Cell = React.memo(function Cell({
  c,
  name,
  isNow,
  width,
  columnHeight,
  cellHeight,
  currentView,
}: CellProps) {
  return (
    <div
      className={classNames(styles.date, {
        [styles.restDay]: c.isRest,
        [styles.lastRestDay]: c.isRest && c.dayIndex === 6,
        [styles.now]: isNow,
        [styles.daily]: currentView === Views.DAILY,
        [styles.monthly]: currentView === Views.MONTHLY,
        [styles.yearly]: currentView === Views.YEARLY,
        [styles.lastMonthDay]: c.isMonthLastDay,
        [styles.lastYearDay]: c.isYearLastDay,
        [styles.weekEndDay]: c.dayIndex === 6,
      })}
      style={{ width, height: columnHeight }}
    >
      {currentView === Views.DAILY ? (
        <>
          <p
            className={styles.heading}
            style={{ height: cellHeight, top: cellHeight + 1 }}
          >
            <span className={styles.dayName}>
              {name} {c.dayLabel}
            </span>
          </p>
          {width / HOURS_PER_DAY >= MIN_HOUR_LINE_WIDTH && (
            <div className={styles.hours}>
              {HOURS.map((hour) => (
                <div key={hour} className={styles.hour}>
                  {width / HOURS_PER_DAY >= MIN_HOUR_LABEL_WIDTH && (
                    <p
                      className={styles.hourLabel}
                      style={{ height: cellHeight }}
                    >
                      {hour}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        currentView !== Views.MONTHLY &&
        currentView !== Views.YEARLY && (
          <p
            className={styles.heading}
            style={{
              height: cellHeight + cellHeight * (1 / 3),
              top: cellHeight + 1,
            }}
          >
            <span className={styles.weeklyName}>{name}</span>
            <span>{c.dayLabel}</span>
          </p>
        )
      )}
    </div>
  );
});

function Calendar({ dates, cell, currentView, height }: Props) {
  const { t } = useTranslation();
  const screen = useScreenWatcher();
  const startIndex = useScrollPos();

  // Heavy stuff survives scroll notifications: recomputed only when the
  // range or locale changes, never when the window moves.
  const cells = React.useMemo(() => buildDateCells(dates), [dates]);
  const shortDays = React.useMemo(
    () => getDaysTexts(t).map((d) => d.slice(0, 3)),
    [t],
  );

  // Hoisted once — the original recomputed this identical expression per cell.
  // DAILY stacks three rows: months, day names, hour labels.
  const headerHeight =
    cell.height +
    1 +
    (currentView === Views.DAILY
      ? cell.height * 2
      : cell.height + cell.height * (1 / 3));
  const columnHeight = height + headerHeight;
  const width = cell.width * dates.length;

  // ceil, not round: round can undercount by half a column. screen.width is
  // the WINDOW width while the graph pane is only ~(100 - panelWidth)% of it,
  // so viewportCols is a safe overestimate — that surplus (not the old code's
  // missing right overscan) is what was preventing blank right edges. Keep
  // the overestimate; the explicit OVERSCAN makes the safety intentional.
  const viewportCols = Math.ceil(screen.width / cell.width);
  const start = Math.max(0, startIndex - OVERSCAN);
  const end = Math.min(cells.length, startIndex + viewportCols + OVERSCAN);

  // Stable identities while the window is static, so the (React.memo'd)
  // Header and the Cells can actually bail out. Add React.memo to Months /
  // Years / DailyMonths at their definitions to collect this win.
  const { cellsChunk, headerDates } = React.useMemo(() => {
    const slice = cells.slice(start, end);
    return { cellsChunk: slice, headerDates: slice.map((c) => c.dt) };
  }, [cells, start, end]);

  // One DateTime.now() per render — the original allocated one PER CELL.
  // A string compare also stays correct across midnight, unlike baking
  // isNow into the prebuilt cells.
  const todayKey = DateTime.now().toISODate();

  const Header = headerMap[currentView];

  return (
    <div
      className={styles.calendar}
      style={{
        height: columnHeight,
        marginLeft: start * cell.width,
        width,
      }}
    >
      <div className={styles.header}>
        <Header dates={headerDates} useWeeks={currentView === Views.MONTHLY} />
      </div>
      <div className={styles.dates}>
        {cellsChunk.map((c) => (
          <Cell
            key={c.key}
            c={c}
            name={shortDays[c.dayIndex]}
            isNow={c.key === todayKey}
            width={cell.width}
            columnHeight={columnHeight}
            cellHeight={cell.height}
            currentView={currentView}
          />
        ))}
      </div>
    </div>
  );
}

export { Calendar };
